import * as apicall from "../components/Apicall";

// The AI support agent's API (utilstop_apiengine, src/routes/ai, mounted at /api/v1/ai).
// The same convention as the other services: a read returns the response's `data` (apicall.get throws on
// an error status); a write returns the whole response, so a screen checks `res.success` and shows
// `res.message` (apicall.post never throws).

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

// ── Reads ───────────────────────────────────────────────────────────────────

// { platform_enabled, schema_ready, languages_available }: whether the AI is installed and switched on.
export const getStatus = async () => unwrap(await apicall.get("ai/status"));

// { configured, reason?, plan_allowed, platform_enabled, settings, usage } for the signed-in vendor.
export const getSettings = async () => unwrap(await apicall.get("ai/settings"));

export const getKb = async () => unwrap(await apicall.get("ai/kb"));
export const getStarterTopics = async () => unwrap(await apicall.get("ai/kb/starter"));

// { months, caps, drafts_30d, conversations_30d }
export const getUsage = async () => unwrap(await apicall.get("ai/usage"));

export const getTranscripts = async ({ before } = {}) =>
  unwrap(await apicall.get(`ai/transcripts${before ? `?before=${encodeURIComponent(before)}` : ""}`));
export const getTranscript = async (id) => unwrap(await apicall.get(`ai/transcripts/${id}`));

export const getCallbacks = async (status = "open") => unwrap(await apicall.get(`ai/callbacks?status=${encodeURIComponent(status)}`));

// What the Channels dashboard needs. Both answer with nothing useful (and never throw) when the AI is not
// installed or the vendor has no AI settings, so the dashboard simply shows no AI extras.
export const getChatInfo = async (customerId) => {
  try {
    const data = unwrap(await apicall.get(`ai/chat_info/${customerId}`));
    return data && data.available ? data : null;
  } catch (e) {
    return null;
  }
};

export const getChatBadges = async (customerIds) => {
  const ids = (customerIds || []).filter((id) => id !== undefined && id !== null);
  if (!ids.length) return {};
  try {
    const res = await apicall.post({ customer_ids: ids }, "ai/chat_badges");
    return res && res.success && res.data ? res.data : {};
  } catch (e) {
    return {};
  }
};

// ── Writes ──────────────────────────────────────────────────────────────────

export const saveSettings = (body) => apicall.post(body, "ai/settings");

export const createArticle = (body) => apicall.post(body, "ai/kb");
export const updateArticle = (id, body) => apicall.post(body, `ai/kb/${id}`);
export const deleteArticle = (id) => apicall.post({}, `ai/kb/${id}/delete`);
export const importStarter = () => apicall.post({}, "ai/kb/import_starter");

export const reviewTranscript = (id, tag, note) => apicall.post({ tag, note }, `ai/transcripts/${id}/review`);

// status: sent | edited | discarded (what staff did with the AI's draft)
export const draftOutcome = (id, status, sentText) => apicall.post({ status, sent_text: sentText }, `ai/drafts/${id}/outcome`);
export const returnToAi = (customerId) => apicall.post({ customer_id: customerId }, "ai/return_to_ai");
export const setCallbackStatus = (id, status) => apicall.post({ status }, `ai/callbacks/${id}/status`);
